import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { PageHeader, Card, Table, Td, Badge, Empty, Btn } from "../../components/UI";
import { ClinicalTabs, useClients } from "./shared";
import { FSelect } from "../engagement/FormControls";
import { ConfirmDialog } from "../../components/SlidePanel";
import { Plus, Trash2, Lock, ShieldAlert } from "lucide-react";
import { VersionAccordion } from "../../components/VersionAccordion";

const CP_COLS = 9;

export default function CrisisPlans() {
  const navigate = useNavigate();
  const clients = useClients();
  const [rows, setRows] = useState([]);
  const [clientFilter, setClientFilter] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    if (clientFilter) {
      api.get(`/clients/${clientFilter}/crisis-plans`).then((r) => { setRows(r.data); setLoading(false); })
        .catch((e) => { toast.error(apiError(e)); setLoading(false); });
    } else {
      // Aggregate across all clients client-side (matches how AllDocuments does it)
      Promise.all(clients.map((c) => api.get(`/clients/${c.id}/crisis-plans`).then((r) => r.data).catch(() => [])))
        .then((lists) => { setRows(lists.flat().sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))); setLoading(false); });
    }
  };
  useEffect(load, [clientFilter, clients.length]); // eslint-disable-line

  const doDelete = async () => {
    try {
      await api.delete(`/crisis-plans/${confirmDelete.id}`);
      toast.success("Crisis plan deleted");
      setConfirmDelete(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="crisis-plans-page">
      <PageHeader title="Crisis or Safety Plans"
        subtitle="Stanley-Brown style structured plans with lock, amend, and version history"
        action={<Btn data-testid="crisis-plan-new-btn"
          onClick={() => navigate("/clinical/crisis-plans/new")}>
          <Plus size={15} /> New Crisis Plan
        </Btn>} />
      <ClinicalTabs />
      <Card>
        <div className="flex flex-wrap gap-3 p-4">
          <FSelect data-testid="cp-client-filter" className="w-56" placeholder="All Clients"
            value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
          </FSelect>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-[#7B6157]">Loading crisis plans…</div>
        ) : rows.length === 0 ? (
          <Empty text="No crisis or safety plans yet. Click New Crisis Plan to start." />
        ) : (
          <Table headers={["", "Title", "Client", "Plan Date", "Status", "Locked", "Versions", "Signed By", ""]}>
            {rows.map((p) => (
              <VersionAccordion key={p.id} record={p} kind="crisis_plan" colSpan={CP_COLS}
                primaryTestId={`cp-row-${p.id}`} rowClassName="hover:bg-[#FDFCF8] transition-colors"
                renderPrimary={({ expandToggle, versionBadge }) => (
                  <>
                    <Td className="w-8">{expandToggle}</Td>
                    <Td className="font-medium text-[#3E2723] cursor-pointer"
                      onClick={() => navigate(`/clinical/crisis-plans/${p.id}`)}>
                      <div className="flex items-center gap-1.5">
                        <ShieldAlert size={13} className="text-[#8D5A00]" />
                        {p.title || "Crisis or Safety Plan"}
                        {versionBadge}
                      </div>
                    </Td>
                    <Td className="cursor-pointer" onClick={() => navigate(`/clinical/crisis-plans/${p.id}`)}>
                      {p.client_name || "-"}
                    </Td>
                    <Td>{p.plan_date ? dayjs(p.plan_date).format("MM/DD/YYYY") : "-"}</Td>
                    <Td><Badge value={p.status || "draft"} /></Td>
                    <Td>
                      {p.locked ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#EDE7DC] px-2 py-0.5 text-[10px] font-bold text-[#4E342E]">
                          <Lock size={10} /> Locked
                        </span>
                      ) : <span className="text-[10px] text-[#7B6157]">Editable</span>}
                    </Td>
                    <Td>
                      {(p.version_count || 1) > 1 ? (
                        <span className="rounded-full bg-[#FFF3E0] px-2 py-0.5 text-[10px] font-bold text-[#8D5A00]">
                          v{p.version_count}
                        </span>
                      ) : <span className="text-[10px] text-[#7B6157]">v1</span>}
                    </Td>
                    <Td className="text-[#7B6157]">
                      {p.signed_by_name || "-"}
                      {p.signed_at && <div className="text-[10px]">{dayjs(p.signed_at).format("MM/DD/YYYY h:mm A")}</div>}
                    </Td>
                    <Td>
                      {!p.locked && (
                        <button type="button" data-testid={`cp-delete-${p.id}`}
                          onClick={() => setConfirmDelete(p)}
                          className="rounded-lg p-1.5 text-[#C62828] hover:bg-[#FFEBEE]">
                          <Trash2 size={15} />
                        </button>
                      )}
                    </Td>
                  </>
                )}
              />
            ))}
          </Table>
        )}
      </Card>
      <ConfirmDialog open={!!confirmDelete} onCancel={() => setConfirmDelete(null)} onConfirm={doDelete}
        title="Delete crisis plan"
        description={confirmDelete ? `Delete this crisis plan for ${confirmDelete.client_name}? This cannot be undone.` : ""} />
    </div>
  );
}
